import { Box, Button, MenuItem, FormControl, Select, TextField, Typography, CircularProgress, Collapse, Chip, Tooltip, IconButton, useTheme, Dialog, useMediaQuery, FormControlLabel, RadioGroup, Radio } from '@mui/material'
import { DatePicker, DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { useAssignmentStore } from '../../store/assignment.store'
import uploadAssignmentsImg from '../../assets/upload-assignment.svg'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle'
import DataLoadingSpinner from '../common/DataLoadingSpinner'
import { CHIP_COLORS, getFileSizeFromBytes, Icons } from '../../utils/utilities'
import { useUserStore } from '../../store/user.store'
import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { ErrorMessage, Field, Form, Formik } from 'formik'
import { observer } from 'mobx-react'
import * as Yup from 'yup'
import dayjs from 'dayjs'
import { RefreshOutlined } from '@mui/icons-material'
import CustomSelectField from '../common/CustomSelectField'
import CustomChip from '../common/CustomChip'

const submissionModes = [
    { label: "Online", value: "online", description: `Students scan their assignments and upload the digital copies via the Alive assignments page.` },
    { label: "Offline", value: "offline", description: <span>Students complete their assignments and submit the physical <em>yellow books</em> directly to the faculty.</span> },
]

const accessGroupsFilterFunc = ( item, values, filterEntity ) => {

    if ( filterEntity === "batch" ) {
        if ( item.entity !== 'batch' ) return false

        if ( !item.batch_assignment_id ) return false

        if ( +item.ac_year_id !== +values.academicYear ) return false

        if ( values.year && item.current_year !== values.year ) return false

        if ( values.sem && item.current_sem && values.sem !== item.current_sem ) return false

        return true
    } else {

        if ( item.entity !== 'section' ) return false

        if ( !item.section_assignment_id ) return false

        if ( +item.ac_year_id !== +values.academicYear ) return false

        if ( values.year && item.current_year !== values.year ) return false

        if ( item.current_sem && values.sem && values.sem !== item.current_sem ) return false

        return true
    }
}

const CreateOrUpdateAssignment = observer( ( { assignment = undefined, subject = undefined, setView, view } ) => {

    const AssignmentStore = useAssignmentStore()
    const UserStore = useUserStore()

    const navigate = useNavigate()
    const { palette, border, breakpoints, layout, components } = useTheme()
    const fullscreen = useMediaQuery( breakpoints.down( 'sm' ) )


    const [loaded, setLoaded] = useState( false )
    const [dropping, setDropping] = useState( false )
    const [uploadStatus, setUploadStatus] = useState( false )
    const [academicYearId, setAcademicYearId] = useState( UserStore.getUserAcademicYears[0]?.ac_year_id || "" )
    const [refreshing, setRefreshing] = useState( false )
    const [isLoadingBatches, setIsLoadingBatches] = useState( false )


    const initialValues = {
        assignment_name: "",
        assignment_details: "",
        assignment_subject: subject || "",
        assignment_batch_id: [],
        assignment_due_date: view === 'update' ? dayjs( AssignmentStore.getSelectedAssignment.assignment_due_date ) : dayjs(),
        academicYear: UserStore.getUserAcademicYears[0]?.ac_year_id || "",
        assignment_submission_mode: 'online',
        attachments: [],
        year: '',
        sem: ''
    }

    const validationSchema = Yup.object().shape( {
        assignment_name: Yup.string( "" ).required( "Assignment name is required!" ),
        assignment_details: Yup.string().required( "Assignment details is required" ),
        assignment_batch_id: Yup.array().required( "Specify the assignment batch" ).test( "TEST_FOR_BATCHES", "Specify the assignment atleast one batch", ( val ) => {
            if ( val.length > 0 )
                return true
            return false
        } ),
        assignment_due_date: Yup.date().required( "Specify the assignment's due date" ),
        assignment_subject: Yup.mixed().required( "Specify the subject" ),
        attachments: Yup.array().required( "Please select assignment attachment" ).test( "CHECK_ATTACHMENT", "Please select assignment attachment", ( val ) => {
            if ( val?.length > 0 )
                return true
            return false
        } )
    } )

    const setAssignmentSubmissionDate = ( val, setFieldValue ) => {
        setFieldValue( 'assignment_due_date', val )
    }

    const removeSelectedAccessGroup = ( event, index, values, setFieldValue ) => {
        event.stopPropagation()
        const newSelectedIds = values.selected_access_groups || []
        const newSelectedGroups = values.assignment_batch_id || []
        newSelectedIds.splice( index, 1 )
        newSelectedGroups.splice( index, 1 )
        setFieldValue( 'assignment_batch_id', newSelectedIds )
        setFieldValue( 'assignment_batch_id', newSelectedGroups )
    }


    useEffect( () => {
        const fetchAcademicYearAndSubjects = async () => {
            try {
                if ( UserStore.getErrorsStatuses?.academicYearsAndBatches === true )
                    await UserStore.fetchUsersAllBatchesAndSectionList()
                if ( UserStore.getErrorsStatuses?.academicYears === true )
                    await UserStore.fetchUserAcademicYears()
                if ( UserStore.getErrorsStatuses?.subjects === true )
                    await UserStore.fetchUserSubjects()

            } catch ( err ) {
                console.log( err )
                if ( err?.response?.status === 401 || err?.response?.data?.unauth ) {
                    UserStore.setERPSessionState( 'ended' )
                } else toast( err?.response?.data?.message || 'Something went wrong while fetching required details!' )
            } finally {
                setLoaded( true )
            }
        }
        fetchAcademicYearAndSubjects()
    }, [AssignmentStore, navigate, view, UserStore] )


    const uploadAssignment = async ( values, { resetForm } ) => {
        setUploadStatus( true )
        const formData = new FormData()

        for ( let i = 0; i < values.attachments.length; i++ ) {
            formData.append( "attachments", values.attachments[i] )
        }

        formData.append( "assignment_name", values.assignment_name )
        formData.append( "assignment_details", values.assignment_details )
        formData.append( "assignment_batch_id", values.assignment_batch_id?.join( "," ) )
        formData.append( "assignment_due_date", values.assignment_due_date )
        formData.append( "assignment_submission_mode", values.assignment_submission_mode )
        formData.append( "assignment_subject", JSON.stringify( values.assignment_subject ) )
        try {
            if ( await AssignmentStore.uploadAssignment( formData, subject?.subject_id || null ) ) {
                await AssignmentStore.fetchAssignmentByAuthor()
                resetForm()
                setView( "manage" )
            }
        } catch ( err ) {
            toast( err?.response?.data?.message || "Something went wrong while creating the assignment" )
        } finally {
            setUploadStatus( false )
        }
    }

    const handleFileInputChange = ( e, value, setFieldValue ) => {
        const files = e.target.files
        let selectedFileList = []
        let invalidFormatFilesList = []

        for ( const file of files ) {
            if ( ['pdf', 'docx'].includes( file.name?.split( "." ).pop()?.toLowerCase() ) && file.size < 50 * 1024 * 1024 )
                selectedFileList.push( file )
            else
                invalidFormatFilesList.push( file.name )
        }
        if ( invalidFormatFilesList.length > 0 )
            toast( <p>Found unsupported or large file(s): <font color={palette.customThemeColor.main}><strong><em>{invalidFormatFilesList.join( "," )}</em></strong></font></p> )
        setFieldValue( 'attachments', [...value, ...selectedFileList] )
        e.target.value = ''
    }

    const handleAcademicYearChange = async ( e, setFieldValue ) => {
        setIsLoadingBatches( true )
        setFieldValue( 'assignment_subject', '' )
        setFieldValue( 'academicYear', e.target.value )
        setFieldValue( 'year', '' )
        setFieldValue( 'sem', '' )
        setAcademicYearId( e.target.value )
        // await UserStore.fetchUserBatchesAndSection( e.target.value )
        setIsLoadingBatches( false )
    }

    const handleSubjectChange = ( e, setFieldValue ) => {
        const subject = UserStore.getUserSubjectsDetails?.currentSubjects?.find( s => s.unique_subject_key === e.target.value )
        setFieldValue( 'assignment_subject', subject )
        setFieldValue( 'year', subject.year )
        setFieldValue( 'sem', subject.sem )
    }

    const refreshYears = async () => {
        setRefreshing( "years" )
        await UserStore.fetchUserAcademicYears( true )
        setRefreshing( null )
    }

    const refreshBatches = async () => {
        if ( academicYearId ) {
            setRefreshing( "batches" )
            await UserStore.fetchUserBatchesAndSection( academicYearId, true )
            setRefreshing( null )
        }
    }

    const removeAttachment = ( values, index, setFieldValue ) => {
        const updatedAttachments = [...values.attachments]
        updatedAttachments.splice( index, 1 )
        setFieldValue( 'attachments', updatedAttachments )
    }

    const updateAssignment = async ( values ) => {
        setUploadStatus( true )
        let payload = {
            "assignment_due_date": values.assignment_due_date,
            "assignment_submission_mode": values.assignment_submission_mode,
        }
        await AssignmentStore.updateAssignmentDetail( payload )
        AssignmentStore.setUpdateStatus( AssignmentStore.selectedAssignment, false )
        AssignmentStore.fetchAssignmentByAuthor()
        setUploadStatus( false )
        setView( "manage" )
    }

    return (
        <Dialog fullScreen={fullscreen} open={['update', 'create'].includes( view )} sx={{ padding: "0" }} >
            <Box sx={{ padding: 0 }} bgcolor={palette.form.formCardBg}>
                <Box position="sticky" sx={{ zIndex: "10" }} top="0" borderBottom={`1px solid ${palette.borderColor}`} bgcolor={palette.form.formCardBg} padding="10px 20px" gap="20px" display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                        <Typography variant="h1" fontWeight="600" component="h1" fontSize="16px">{view === 'update' ? "Update assignment" : "Create new assignment"}</Typography>
                        <Typography variant="subtitle2" fontSize="12px" color='textSecondary' >Create assignment by providing all required details</Typography>
                    </Box>
                    <Tooltip placement='top' title="Back to assignments">
                        <IconButton onClick={() => setView( "manage" )} size='small'>
                            {Icons.default.CloseIcon}
                        </IconButton>
                    </Tooltip>
                </Box>
                {loaded && <Box padding="20px">
                    <Formik validationSchema={validationSchema} initialValues={initialValues} onSubmit={view === 'update' ? updateAssignment : uploadAssignment}>
                        {( { values, setFieldValue } ) => (
                            <Form>
                                <Box>
                                    <FormControl fullWidth>
                                        <Typography htmlFor="name" gutterBottom variant='subtitle2' component="label" color={palette.form.labelColor}>Enter a clear and concise assignment title *</Typography>
                                        <Field size="small" as={TextField} type="text"
                                            placeholder="Eg: DS assignment"
                                            InputLabelProps={{ sx: { fontSize: "14px" } }}
                                            value={values.assignment_name}
                                            id="name"
                                            name="assignment_name"
                                            disabled={view === 'update'}
                                        />
                                        <Typography component="span" color="errorMessage.main" marginTop="5px" fontSize="12px"> <ErrorMessage name="assignment_name" /> </Typography>
                                    </FormControl>
                                </Box>
                                <Box>
                                    <FormControl margin='normal' fullWidth>
                                        <Typography htmlFor="outlined-multiline-static" gutterBottom variant='subtitle2' component="label" color={palette.form.labelColor}>
                                            Describe the assignment details here *
                                        </Typography>
                                        <Field size="small" as={TextField}
                                            placeholder="Eg: Complete the assignment and upload before due date!"
                                            InputLabelProps={{ sx: { fontSize: "14px" } }}
                                            id="outlined-multiline-static"
                                            multiline
                                            rows={4}
                                            name="assignment_details"
                                            value={values.assignment_details}
                                            disabled={view === 'update'}
                                        />
                                        <Typography component="span" color="errorMessage.main" marginTop="5px" fontSize="12px"> <ErrorMessage name="assignment_details" /> </Typography>
                                    </FormControl>
                                </Box>

                                {view !== 'update' && <Box>
                                    <FormControl margin='normal' fullWidth>
                                        <Box display="flex" justifyContent="space-between" alignItems="center" gap="5px">
                                            <Typography htmlFor="ac-year-id" gutterBottom variant='subtitle2' component="label" color={palette.form.labelColor}>
                                                Select academic year
                                            </Typography>
                                            <Button id="academicYearRefreshBtn" disabled={refreshing === "years"} startIcon={refreshing === "years" ? <CircularProgress size={14} /> : Icons.default.Sync} onClick={refreshYears} sx={{ width: "fit-content", flexShrink: 0, fontSize: "12px" }} color="secondary" >
                                                {refreshing === "years" ? "Refreshing academic years..." : "Refresh academic years"}
                                            </Button>
                                        </Box>
                                        <CustomSelectField itemValueKey={"ac_year_id"} renderOptionText={item => item.ac_year} data={UserStore.getUserAcademicYears} placeholder='Academic Year' size="small" onChange={event => handleAcademicYearChange( event, setFieldValue )} value={academicYearId} />
                                    </FormControl>
                                </Box>}

                                {view !== 'update' && <Box>
                                    <FormControl margin='normal' fullWidth>
                                        <Typography htmlFor="subject" gutterBottom variant='subtitle2' component="label" color={palette.form.labelColor}>
                                            Select the subject from academic year {UserStore.getUserAcademicYears?.find( acYear => acYear.ac_year_id === academicYearId )?.ac_year} relevant to the assignment *
                                        </Typography>
                                        <CustomSelectField
                                            id="subjectSelector"
                                            size="small"
                                            renderValue={selected => {
                                                if ( selected ) {
                                                    const subject = UserStore.getUserSubjectsDetails?.currentSubjects.find( sub => sub.unique_subject_key === selected )
                                                    if ( subject )
                                                        return `${subject.subject_name?.toLowerCase()} (${subject.subject_code})`
                                                    else
                                                        return "Subject"
                                                } else {
                                                    return "Subject"
                                                }
                                            }}
                                            itemValueKey="unique_subject_key"
                                            itemIdentityKey="unique_subject_key"
                                            data={( UserStore.getUserSubjectsDetails?.currentSubjects || [] ).filter( sub => +sub.ac_year_id === +academicYearId )}
                                            sx={{ color: !values.subject && palette.form.placeholder, textTransform: "capitalize" }}
                                            value={values?.assignment_subject?.unique_subject_key || ""}
                                            onChange={event => handleSubjectChange( event, setFieldValue )}
                                            name="subject"
                                            renderOptionText={item => <Box display="flex" flexDirection="column">
                                                <Typography textTransform="capitalize" variant='subtitle2'>
                                                    {item.subject_name?.toLowerCase()}
                                                </Typography>
                                                <Box display="flex" gap="5px" alignItems="center">
                                                    <CustomChip tooltipTitle='Subject code' value={item.subject_code} />
                                                    <CustomChip tooltipTitle='Year' value={`Year: ${item.year}`} />
                                                    {item.sem && <CustomChip tooltipTitle='Semester' value={`Sem: ${item.sem}`} />}
                                                </Box>
                                            </Box>}
                                        />
                                        <Typography component="span" color="errorMessage.main" marginTop="5px" fontSize="12px"> <ErrorMessage name="assignment_subject" /> </Typography>

                                    </FormControl>
                                </Box>}



                                {view !== 'update' && academicYearId && <Box>
                                    <FormControl margin='normal' fullWidth>
                                        <Box display="flex" alignItems="center" gap="5px">
                                            <Typography gutterBottom variant='subtitle2' component="label" color={palette.form.labelColor}>
                                                Select the student batches or sections for this assignment *
                                            </Typography>
                                            {academicYearId && <Typography onClick={refreshBatches} sx={{ cursor: "pointer", '&:hover': { textDecoration: "underline" } }} display="flex" gap="5px" alignItems="center" fontSize="14px" color={"secondary"} fontWeight="500"> {refreshing === "batches" ? <CircularProgress size={16} /> : <RefreshOutlined fontSize='16px' />}Refresh batches</Typography>}
                                        </Box>
                                        <CustomSelectField
                                            onChange={event => setFieldValue( 'assignment_batch_id', event.target.value )}
                                            options={{ showCheckbox: true }}
                                            disabled={isLoadingBatches}
                                            slotProps={{
                                                input: {
                                                    sx: {
                                                        padding: values.assignment_batch_id?.length > 0 ? "5px" : ""
                                                    }
                                                }
                                            }}
                                            searchFilter={( item, searchTerm ) => {
                                                return item.short_title?.toLowerCase()?.includes( searchTerm?.toLowerCase() )
                                            }}
                                            noOptionsMessage="We couldn't find any sections or batches for the year and semester of the selected subject."
                                            renderValue={value => {
                                                if ( value?.length === 0 ) {
                                                    return "Select batches and sections."
                                                } else {
                                                    return <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: "5px" }}>
                                                        {values.assignment_batch_id.map( ( agId, index ) => {

                                                            const ag = UserStore.getUsersAllBatchesAndSectionList?.find( item => item.id === agId )

                                                            return (
                                                                <Box padding="2px 5px" bgcolor={palette.contentBg} border={border[1]} borderRadius="10px" key={ag.id} display="flex" alignItems="center">
                                                                    <Typography fontSize="12px" variant='subtitle2'>
                                                                        {ag.chip_label}
                                                                    </Typography>
                                                                    <IconButton onMouseDown={e => e.stopPropagation()} onClick={event => removeSelectedAccessGroup( event, index, values, setFieldValue )} size='small' color='error'>
                                                                        {Icons.default.CancelIcon}
                                                                    </IconButton>
                                                                </Box>
                                                            )
                                                        } )}
                                                    </Box>
                                                }
                                            }}
                                            size="small"
                                            value={values.assignment_batch_id || ""}
                                            data={{
                                                sections: UserStore.getUsersAllBatchesAndSectionList?.filter( item => accessGroupsFilterFunc( item, values, "section" ) ),
                                                batches: UserStore.getUsersAllBatchesAndSectionList?.filter( item => accessGroupsFilterFunc( item, values, "batch" ) )
                                            }}
                                            id="batchesAndSectionsInput"
                                            itemValueKey="id"
                                            multiple
                                            renderOptionText={item => <Box display="flex" flexDirection="column">
                                                <Typography variant='subtitle2'>
                                                    {item.short_title}
                                                </Typography>
                                                <Box display="flex" gap="5px" alignItems="center">
                                                    <CustomChip tooltipTitle='Course short name' value={item.course_name_short} />
                                                    <CustomChip tooltipTitle='Year' value={`Year: ${item.current_year}`} />
                                                    {item.current_year && <CustomChip tooltipTitle='Semester' value={`Sem: ${item.current_sem}`} />}
                                                </Box>
                                            </Box>}
                                        />
                                        <Typography component="span" color="errorMessage.main" marginTop="5px" fontSize="12px"> <ErrorMessage name="assignment_batch_id" /> </Typography>
                                    </FormControl>
                                </Box>}

                                <Box>
                                    <FormControl fullWidth margin='normal'>
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <Typography gutterBottom variant='subtitle2' component="label" color={palette.form.labelColor}>
                                                {view === 'update' ? "Update the submission deadline *" : "Set the submission deadline *"}
                                            </Typography>
                                            <DateTimePicker
                                                sx={{ "width": "100%", fontSize: "14px", '& input': { padding: "10px" } }}
                                                {...( components?.MuiPickerOutlinedInput?.defaultProps || {} )}
                                                minDate={dayjs( new Date() )}
                                                disableToolbar
                                                variant="inline"
                                                format="DD-MM-YYYY hh:mm A"
                                                id="date-picker-inline"
                                                value={dayjs( values.assignment_due_date )}
                                                onChange={( val ) => setAssignmentSubmissionDate( val, setFieldValue )}
                                                KeyboardButtonProps={{
                                                    'aria-label': 'change date',
                                                }}
                                            />
                                        </LocalizationProvider>
                                    </FormControl>
                                    <Typography component="span" color="errorMessage.main" marginTop="5px" fontSize="12px"> <ErrorMessage name="assignment_due_date" /> </Typography>

                                </Box>
                                {view !== 'update' && <Box marginTop='20px'>
                                    <Box onDragOver={() => setDropping( true )} onDrop={() => setDropping( false )} onDragLeave={() => setDropping( false )} sx={{ cursor: "pointer", bgcolor: palette.contentBg, position: "relative", border: `2px dashed ${palette.borderColor}`, borderRadius: "5px", padding: "20px" }} >
                                        <Box component="input" type="file" sx={{ cursor: "pointer", position: "absolute", opacity: 0, top: 0, left: 0, right: 0, bottom: 0 }} onChange={( e ) => handleFileInputChange( e, values.attachments, setFieldValue )} multiple />
                                        <Box display="flex" gap="10px" flexDirection="column" alignItems="center">
                                            {!dropping && <Box display="flex" height="200px" justifyContent="center" gap="5px" flexDirection="column" alignItems="center">
                                                <img src={uploadAssignmentsImg} alt="Upload assignment" width="120px" />
                                                <Typography variant="body1" fontWeight="600" >Upload assignment problem file</Typography>
                                                <Typography variant="subtitle2" color="textSecondary">Drag and drop here or  <Box component="span" sx={{ color: palette.customThemeColor.dark }}><u>Browse</u></Box> </Typography>
                                                <Typography variant="subtitle2" color="textSecondary" fontSize="12px">Supported formats: .pdf and .docx</Typography>
                                                <Typography variant="subtitle2" color="textSecondary" fontSize="12px">Max Size: 50MB</Typography>
                                            </Box>}
                                            {dropping && <Box display="flex" gap="5px" height="200px" justifyContent="center" flexDirection="column" sx={{ pointerEvents: "none" }} alignItems="center">
                                                <Typography variant="body1" fontWeight="600" className="animation-shake"  >Drop your file(s) here.</Typography>
                                                <Typography variant="subtitle2" color="textSecondary" fontSize="12px">Supported formats: .pdf and .docx</Typography>
                                            </Box>}
                                        </Box>
                                    </Box>
                                    <Collapse in={values.attachments.length > 0}>
                                        <Box display="flex" gap="10px" paddingTop="20px" flexWrap="wrap">
                                            {values.attachments.length > 0 && values.attachments.map( ( file, index ) => (
                                                <Box padding="5px 10px" border={`1px solid ${CHIP_COLORS[index % CHIP_COLORS.length]}`} borderRadius={layout.borderRadius} key={index} sx={{ display: "flex", gap: "10px", alignItems: "center", background: CHIP_COLORS[index % CHIP_COLORS.length] + "44" }}  >
                                                    <Box>
                                                        <Typography variant='subtitle2' >{file.name}</Typography>
                                                        <Typography display="block" variant='caption'>{getFileSizeFromBytes( file.size )}</Typography>
                                                    </Box>
                                                    <IconButton size='small' onClick={() => removeAttachment( values, index, setFieldValue )}>
                                                        <RemoveCircleIcon fontSize='small' />
                                                    </IconButton>
                                                </Box>
                                            ) )}
                                        </Box>
                                    </Collapse>
                                    <Typography component="span" color="errorMessage.main" marginTop="5px" fontSize="12px"> <ErrorMessage name="attachments" /> </Typography>
                                </Box>}

                                <FormControl margin='normal'>
                                    <Typography htmlFor="submission-mode" gutterBottom variant='subtitle2' component="label" color={palette.form.labelColor}>
                                        Select assignment submission mode *
                                    </Typography>
                                    <Field as={RadioGroup}
                                        row
                                        aria-labelledby="demo-row-radio-buttons-group-label"
                                        name="assignment_submission_mode"
                                        value={values.assignment_submission_mode}
                                        sx={{
                                            margin: 0,
                                            display: "grid",
                                            gap: "10px",
                                            gridTemplateColumns: { md: "auto auto", xs: "1fr" }
                                        }}
                                    >
                                        {submissionModes.map( submissionMode => (
                                            <FormControlLabel sx={{ margin: 0, border: border[1], borderColor: values.assignment_submission_mode === submissionMode.value && palette.secondary.main, borderRadius: "5px", padding: "10px", gap: "10px", alignItems: "flex-start" }} key={submissionMode.label} value={submissionMode.value} control={<Radio color="secondary" />} label={
                                                <Box flexGrow={1}>
                                                    <Typography display="block" variant='subtitle2'>{submissionMode.label}</Typography>
                                                    <Typography fontSize="10px">{submissionMode.description}</Typography>
                                                </Box>
                                            } />
                                        ) )}
                                    </Field>
                                </FormControl>

                                <Box marginTop="20px">
                                    {
                                        view !== 'update' ?
                                            <Box display="flex" marginTop="40px" gap="10px" alignItems="center" flexWrap="wrap">
                                                <Button startIcon={uploadStatus ? <CircularProgress size={14} /> : Icons.default.AddIcon} disabled={uploadStatus} disableElevation sx={{ textTransform: "capitalize" }} type="submit" variant="contained" color="primary">
                                                    {uploadStatus === false ? 'Create' : "Creating..."}
                                                </Button>
                                                <Button disableElevation disabled={uploadStatus} onClick={() => setView( "manage" )} sx={{ textTransform: "capitalize" }} variant="contained" color="error">
                                                    Cancel
                                                </Button>
                                            </Box> :
                                            <Box display="flex" marginTop="40px" gap="10px" alignItems="center" flexWrap="wrap">
                                                <Button onClick={() => updateAssignment( values )} startIcon={uploadStatus ? <CircularProgress size={14} /> : Icons.default.EditIcon} disabled={uploadStatus} disableElevation sx={{ textTransform: "capitalize" }} variant="contained" color="primary">
                                                    {uploadStatus === false ? 'Update' : "Updating..."}
                                                </Button>
                                                <Button disabled={uploadStatus} onClick={() => setView( "manage" )} sx={{ textTransform: "capitalize" }} variant="contained" disableElevation color="error">
                                                    Cancel
                                                </Button>
                                            </Box>
                                    }

                                </Box>
                            </Form> )}
                    </Formik>
                </Box>}
                {!loaded && <DataLoadingSpinner waitingMessage="Fetching data..." />}
            </Box>
        </Dialog>
    )
} )

export default CreateOrUpdateAssignment
